import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import { Box, Button, FormControlLabel, Switch } from "@mui/material";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxSelect from "../../../../../components/SbuxSelect";
import {
  getLanguagesSelector,
  getSenderNumberSelector,
} from "../../../../../selectors/userInstanceSelector";
import { sendTestSms } from "../../../../../services/testSms";
import { useSnackbars } from "../../../../../hooks/useSnackbar";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import SbuxStackSnackbar from "../../../../../components/SbuxStackSnackbar";
import useTextTranslate from "../../../../content/hooks/useTextTranslate";
import { MIN_PHONE_LENGTH } from "../../../../../constants";
import {
  isPhoneValid,
  sanitizeCountryCode,
  sanitizePhoneNumber,
} from "../../../../../utils/phoneNumber";

interface LanguageItem {
  name: string;
  [key: string]: unknown;
}

// Shared styles
const halfWidthBoxSx = { flex: 1, maxWidth: 300 };

const TestSms = () => {
  const dispatch = useAppDispatch();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbars();
  const messageRequiredError = useTextTranslate("Please enter a message");
  const phoneErrorMessage = useTextTranslate(
    "Please enter a valid phone number (at least 10 digits)",
  );
  const languages =
    (useAppSelector(getLanguagesSelector) as LanguageItem[] | undefined) ?? [];
  const senderNumber = useAppSelector(getSenderNumberSelector);

  const [phoneNumber, setPhoneNumber] = useState({
    countryCode: "+1",
    number: "",
  });
  const [message, setMessage] = useState("");
  const [language, setLanguage] = useState("english");
  const [includeFooter, setIncludeFooter] = useState(true);
  const [sending, setSending] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [messageError, setMessageError] = useState("");
  const hasInitializedLanguage = useRef(false);

  // Sync language when languages load asynchronously
  useEffect(() => {
    if (!hasInitializedLanguage.current && languages.length > 0) {
      hasInitializedLanguage.current = true;
      setLanguage(String(languages[0].name).toLowerCase());
    }
  }, [languages]);

  // Memoize language menu items to avoid recreation on every render
  const languageMenuItems = useMemo(
    () =>
      languages.map((l) => ({
        value: String(l.name).toLowerCase(),
        name: l.name,
      })),
    [languages],
  );

  // Consolidated form validation
  const isFormValid = Boolean(
    message.trim() && phoneNumber.number.trim().length >= MIN_PHONE_LENGTH,
  );

  // Helper to validate phone and set error state
  const validatePhoneAndSetError = useCallback(
    (value: string, required = false): boolean => {
      const valid = isPhoneValid(value, required);
      setPhoneNumberError(valid ? "" : phoneErrorMessage);
      return valid;
    },
    [phoneErrorMessage],
  );

  const handlePhoneNumberChange = useCallback(
    (field: "countryCode" | "number", value: string) => {
      if (field === "countryCode") {
        setPhoneNumber((prev) => ({
          ...prev,
          [field]: sanitizeCountryCode(value),
        }));
      } else if (field === "number") {
        const sanitized = sanitizePhoneNumber(value);
        setPhoneNumber((prev) => ({ ...prev, [field]: sanitized }));
        validatePhoneAndSetError(sanitized);
      }
    },
    [validatePhoneAndSetError],
  );

  const handlePhoneNumberBlur = useCallback(() => {
    validatePhoneAndSetError(phoneNumber.number);
  }, [phoneNumber.number, validatePhoneAndSetError]);

  const handleMessageChange = useCallback((value: string) => {
    setMessage(value);
    // Clear error when user types valid content
    if (value.trim()) {
      setMessageError("");
    }
  }, []);

  const handleMessageBlur = useCallback(() => {
    setMessageError(message.trim() ? "" : messageRequiredError);
  }, [message, messageRequiredError]);

  const handleSend = async () => {
    let hasError = false;

    if (!message.trim()) {
      setMessageError(messageRequiredError);
      hasError = true;
    }

    if (!validatePhoneAndSetError(phoneNumber.number, true)) {
      hasError = true;
    }

    if (hasError) {
      return;
    }

    const fullPhoneNumber = `${
      phoneNumber.countryCode
    }${phoneNumber.number.trim()}`;

    setSending(true);
    try {
      await dispatch(
        sendTestSms({
          to: [fullPhoneNumber],
          message,
          language,
          includeFooter,
          senderNumber: senderNumber ?? "",
        }),
      ).unwrap();
      showSnackbar("SMS was sent successfully");
      setMessage("");
    } catch {
      showSnackbar("Failed to send SMS", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SbuxStackSnackbar
        open={snackbar.open}
        message={
          typeof snackbar.message === "string" &&
          snackbar.message.trim() !== "" ? (
            <SbuxTranslate>{snackbar.message}</SbuxTranslate>
          ) : (
            snackbar.message || ""
          )
        }
        severity={snackbar.severity}
        autoHide={snackbar.autoHide}
        handleClose={hideSnackbar}
      />
      <Box sx={{ padding: 2, maxWidth: 640 }}>
        {/* Language and Sender Number Section */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Box sx={halfWidthBoxSx}>
            <SbuxSelect
              label={<SbuxTranslate>Language</SbuxTranslate>}
              menuItems={languageMenuItems}
              value={language}
              handleSelectChange={(value) => setLanguage(value)}
            />
          </Box>
          <Box sx={halfWidthBoxSx}>
            <SbuxTextField
              label={<SbuxTranslate>Sender Number</SbuxTranslate>}
              value={senderNumber || ""}
              disabled
              preventAutoFocus
              fullWidth
            />
          </Box>
        </Box>

        {/* Phone Number Section */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>
            <SbuxTranslate>Phone Number</SbuxTranslate>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <SbuxTextField
              label={<SbuxTranslate>Country Code</SbuxTranslate>}
              value={phoneNumber.countryCode}
              handleChange={(e) =>
                handlePhoneNumberChange("countryCode", e.target.value)
              }
              sx={{ width: 110 }}
              placeholder="+1"
              data-testid="country-code-input"
            />
            <SbuxTextField
              label={<SbuxTranslate>Phone Number</SbuxTranslate>}
              value={phoneNumber.number}
              required
              preventAutoFocus={true}
              handleChange={(e) =>
                handlePhoneNumberChange("number", e.target.value)
              }
              onBlur={handlePhoneNumberBlur}
              fullWidth
              placeholder="1234567890"
              isError={!!phoneNumberError}
              errorMessage={phoneNumberError}
              data-testid="phone-number-input"
            />
          </Box>
        </Box>

        <SbuxTextField
          label={<SbuxTranslate>Message</SbuxTranslate>}
          placeholder={messageRequiredError}
          value={message}
          required
          handleChange={(e) => handleMessageChange(e.target.value)}
          onBlur={handleMessageBlur}
          fullWidth
          displayCounter={true}
          maxLength={800}
          multiline={true}
          rows={4}
          preventAutoFocus={true}
          sx={{ mb: 2 }}
          data-testid="message-input"
          isError={!!messageError}
          errorMessage={messageError}
        />

        <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeFooter}
                onChange={(e) => setIncludeFooter(e.target.checked)}
              />
            }
            label={<SbuxTranslate>Include Footer</SbuxTranslate>}
          />
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !isFormValid}
            sx={{ width: "auto" }}
          >
            <SbuxTranslate>Send Test SMS</SbuxTranslate>
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default TestSms;
